// Session-related API calls.
//
// The refresh-token endpoint is environment-driven via PUBLIC_REFRESH_TOKEN_URL.
// The response mirrors the login response shape:
//   { success, token, refreshToken, expiresIn, userProfile, redirect, ... }
// We only consume `token` and `refreshToken` here; user profile is
// re-derived on the next login.

const REFRESH_TOKEN_URL = import.meta.env.PUBLIC_REFRESH_TOKEN_URL;
if (!REFRESH_TOKEN_URL) {
  throw new Error(
    "PUBLIC_REFRESH_TOKEN_URL is not set. Set it in your .env file.",
  );
}

/**
 * @typedef {Object} RefreshResponse
 * @property {string} token         - new access token (JWT)
 * @property {string} [refreshToken] - new refresh token (rotated)
 */

/**
 * Calls the backend refresh-token endpoint.
 *
 * @param {{ refreshToken: string }} payload
 * @returns {Promise<RefreshResponse>}
 * @throws {Error} if the response is missing `token` or the request fails
 */
export const refreshTokenRequest = async ({ refreshToken }) => {
  if (!refreshToken) {
    throw new Error("refreshToken is required");
  }

  const response = await fetch(REFRESH_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok || !data?.token) {
    const message = data?.message || "Refresh failed";
    const error = new Error(message);
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return {
    token: data.token,
    refreshToken: data.refreshToken || data.refresh || null,
  };
};
